import type { UseMutationOptions } from '@tanstack/react-query';
import type {
    ClientPathsWithMethod,
    FetchOptions,
    FetchResponse, // Needed for response types
    MaybeOptionalInit, // Needed for input type
} from 'openapi-fetch';
import type { MediaType } from 'openapi-typescript-helpers';

import type { Client } from '../'; // Use the react-query enabled api object
import type { paths } from '../api.generated';

import { api } from '../';
import { logger } from '../lib/logger';
import { deepMerge, setValueByPath } from '../lib/utils';

// --- Type Definitions ---

// Base type for PATCH URLs from your Client
type ApiPatchUrl = ClientPathsWithMethod<Client, 'patch'>;

// --- Helper Types to derive Mutation specifics based on Path ---

// Get the specific PATCH operation definition from the Client paths
// Assumes Client['paths'][TPath]['patch'] exists and follows OpenAPI structure
type PatchOperation<TPath extends ApiPatchUrl> = paths[TPath]['patch'];

// Infer the type of the variables (request body/params) for the mutation
type MutationVariables<TPath extends ApiPatchUrl> = MaybeOptionalInit<
    paths[TPath],
    'patch'
>;

// Infer the type of the data returned on successful mutation
type MutationData<TPath extends ApiPatchUrl> = Required<
    FetchResponse<PatchOperation<TPath>, MutationVariables<TPath>, MediaType>
>['data'];

// Infer the type of the error returned on failed mutation
type MutationError<TPath extends ApiPatchUrl> = Required<
    FetchResponse<PatchOperation<TPath>, MutationVariables<TPath>, MediaType>
>['error'];

// Define the specific type for mutation options passed to usePatcher
// Omit keys that are handled internally by api.useMutation
type PatcherMutationOptions<TPath extends ApiPatchUrl> = Omit<
    UseMutationOptions<
        MutationData<TPath>,
        MutationError<TPath>,
        MutationVariables<TPath>
    >,
    'mutationFn' | 'mutationKey'
>;

// --- Updated Hook ---

/**
 * React hook for patching data using a PATCH request.
 * Handles nested fields, optional value manipulation, and ModelState error extraction.
 * Invalidates all queries on successful patch by default (can be overridden).
 *
 * @template TPath The specific API endpoint URL for the PATCH request.
 * @param {TPath} url - The URL for the PATCH request, inferred from the Client paths.
 * @param {PatcherMutationOptions<TPath>} [mutationOptions] Optional mutation options for react-query.
 * @param {FetchOptions<PatchOperation<TPath>>} [defaultFetchOptions] Optional default fetch options.
 * @returns An object containing the patch function, patchField function, loading/error states, and an error getter.
 */
export function usePatcher<TPath extends ApiPatchUrl>(
    url: TPath,
    mutationOptions?: PatcherMutationOptions<TPath>,
    defaultFetchOptions?: FetchOptions<PatchOperation<TPath>>,
) {
    // Optional: Get queryClient if you want default invalidation
    // const queryClient = useQueryClient();
    // api.useMutation correctly infers types based on 'patch' and the specific 'url' (TPath)
    const mutation = api.useMutation('patch', url, {
        ...mutationOptions,
        scope: {
            id: mutationOptions?.scope?.id ?? '',
        },
    });

    /**
     * Function to trigger the patch operation for a partial object.
     *
     * @param partialObject The partial object to patch with multiple fields.
     * @param valueManipulationFunction Optional function to transform the entire object before sending.
     */
    async function patch(
        partialObject: Record<string, unknown>,
        valueManipulationFunction?: (value: Record<string, unknown>) => Record<string, unknown>,
    ) {
        const processedObject = valueManipulationFunction
            ? valueManipulationFunction(partialObject)
            : partialObject;

        const targetFetchOptions = { body: processedObject };

        const mergedFetchOptions = (defaultFetchOptions
            ? deepMerge(
                    targetFetchOptions,
                    defaultFetchOptions,
                )
            : targetFetchOptions) as FetchOptions<PatchOperation<TPath>>;

        addClearFields(mergedFetchOptions);

        try {
            await mutation.mutateAsync(mergedFetchOptions);
        }
        catch (error) {
            // Handle the error from the mutation
            // Optionally, you can set an internal error state here
            return error; // Return the error for testing purposes
        }

        return undefined; // Return undefined on success
    }

    /**
     * Function to trigger the patch operation for a specific field.
     *
     * @param fieldName The name of the field to patch (can be nested, e.g., "address.street").
     * @param valueManipulationFunction Optional function to transform the value before sending.
     */
    function patchField(
        fieldName: string,
        valueManipulationFunction?: (value: unknown) => unknown,
    ) {
        /**
         * The actual mutation trigger function for a specific field value.
         * @param { value: unknown } field The value to set for the field.
         */
        async function mutate({ value: fieldValue }: { value: unknown }) {
            const value = valueManipulationFunction
                ? valueManipulationFunction(fieldValue)
                : fieldValue;

            const bodyResult = setValueByPath(fieldName, value);

            if (bodyResult.isErr()) {
                // Handle the error from setValueByPath appropriately
                // Maybe set an internal error state or throw?
                logger.error(['Error constructing patch object:', bodyResult.error.message]);

                return 'Er is iets mis gegaan met het opslaan...';
            }

            const targetFetchOptions = { body: bodyResult.value };

            const mergedFetchOptions = (defaultFetchOptions
                ? deepMerge(
                        targetFetchOptions,
                        defaultFetchOptions,
                    )
                : targetFetchOptions) as FetchOptions<PatchOperation<TPath>>;

            addClearFields(mergedFetchOptions);

            try {
                await mutation.mutateAsync(mergedFetchOptions);
            }
            catch (error) {
                // Handle the error from the mutation
                // Optionally, you can set an internal error state here
                return getFieldError(fieldName, error); // Return the error for testing purposes
            }

            return undefined; // Return the error for testing purposes
        }

        return mutate;
    }

    patchField.debounceTime = 500 as const; // Optional: Add debounce time if needed
    patch.debounceTime = 500 as const; // Optional: Add debounce time if needed

    /**
     * Gets the specific ModelState validation error message for a given field, if any.
     * @param _fieldName The field name to check for errors (e.g., "email").
     * @returns The first error message string or undefined.
     * @todo Implement robust error parsing based on your API's error structure.
     */
    function getFieldError(_fieldName: string, error: unknown): string | undefined {
        // Als dit voorkomt, dan is alles volledig fucked, zou nooit moeten gebeuren
        if (typeof error !== 'object' || error === null)
            return 'Een onbekende fout heeft zich voorgedaan (1)';

        // In dit geval ligt de error niet aan een van de velden, maar aan de API zelf
        if (!('ModelState' in error))
            return 'Een onbekende fout heeft zich voorgedaan (2)';

        // In dit geval is het er een error met een veld, maar niet het veld wat we insturen
        const errorWithModelState = error as { ModelState: Record<string, unknown> };
        if (!(`command.${_fieldName}` in errorWithModelState.ModelState))
            return 'Een onbekende fout heeft zich voorgedaan (3)';

        const modelState = errorWithModelState.ModelState[`command.${_fieldName}`];

        // In deze gevallen heeft de achterkant geen error die wij kunnen parsen
        if (!Array.isArray(modelState))
            return 'Een onbekende fout heeft zich voorgedaan (4)';
        if (modelState.length === 0)
            return 'Een onbekende fout heeft zich voorgedaan (5)';

        return modelState.join(', ');
    }

    return {
        patch,
        patchField,
        getFieldError,
        ...mutation,
    };
}

// Helper function to add clearFields array to the body if any key has a null value, recursively
export function addClearFields<TPath extends ApiPatchUrl>(
    fetchOptions: FetchOptions<PatchOperation<TPath>>,
    squashNestedKeys: boolean = true, // Toggle to squash nested keys or not
) {
    if (fetchOptions.body && typeof fetchOptions.body === 'object') {
        const clearFields: string[] = [];

        function traverseAndCollectNulls(obj: Record<string, any>, path: string[] = []) {
            for (const [key, value] of Object.entries(obj)) {
                const currentPath = squashNestedKeys ? [key] : [...path, key];
                if (value === null) {
                    clearFields.push(currentPath.join('.'));
                }
                else if (typeof value === 'object' && value !== null) {
                    traverseAndCollectNulls(value, currentPath);
                }
            }
        }

        traverseAndCollectNulls(fetchOptions.body);

        if (clearFields.length > 0) {
            // @ts-expect-error: The type is to confusing for TS aka skill issue on our side 😉
            fetchOptions.body.clearFields = clearFields;
        }
    }
}
